import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Group, Text, ThemeIcon } from '@mantine/core';
import { IconBuildingWarehouse } from '@tabler/icons-react';
import { formatOrgName } from '@/lib/format';
import IdText from '@/components/common/IdText';

/** Минимум, которым сторона сделки представляется в интерфейсе. */
interface Party {
  readonly name?: string | null;
  readonly inn?: string | null;
}

interface PartyIdentityProps {
  readonly party: Party | undefined | null;
  /** i18n-ключ подписи над реквизитами. По умолчанию «Экспедитор». */
  readonly labelKey?: string;
  /** Значок стороны. По умолчанию — склад (экспедитор). */
  readonly icon?: ReactNode;
}

// Реквизиты назначенной стороны (наименование + ИНН). Общий блок: экспедитор на шагах «Назначение
// экспедитора» и подписи поручения, перевозчик — на шаге «Назначение перевозчика». Null, если сторона
// не назначена ИЛИ реквизиты не положены смотрящему (сервер в этом случае не присылает объект вовсе).
export default function PartyIdentity({ party, labelKey, icon }: PartyIdentityProps) {
  const { t } = useTranslation();
  if (!party || (!party.name && !party.inn)) {
    return null;
  }
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon size={32} radius="md" variant="light" color="brand">
        {icon ?? <IconBuildingWarehouse size={18} />}
      </ThemeIcon>
      <Box style={{ minWidth: 0 }}>
        <Text fz="xs" c="gray.7">
          {t(labelKey ?? 'app.deals.forwarding.forwarder')}
        </Text>
        {party.name ? (
          <Text fz="sm" fw={600} lineClamp={2}>
            {formatOrgName(party.name)}
          </Text>
        ) : null}
        {party.inn ? (
          <Text fz="sm" c="gray.8">
            {t('auth.fields.inn')} <IdText>{party.inn}</IdText>
          </Text>
        ) : null}
      </Box>
    </Group>
  );
}
